import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../DoctorForm/DoctorForm.css";
import Avatar from "../../assets/avatar.png";
import axios from "axios";
import CustomModal from "../../components/modal/Modal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LocationPicker from "../../components/locationPicker/LocationPicker";
import { MapPin } from "lucide-react";
import { apiUrl } from "../../config/appConfig";

const PharmacyForm = () => {
  const [fullName, setFullName] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState("");
  const [hospital, setHospital] = useState("");
  const [location, setLocation] = useState("");
  const [onlineConsult, setOnlineConsult] = useState("No");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState(""); // optional
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const fileInputRef = useRef(null);
  const [countryCode, setCountryCode] = useState("+964");
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [profilePicError, setProfilePicError] = useState("");
  const [hasCamera, setHasCamera] = useState(true);
  const [locationError, setLocationError] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const sessionTokenRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [region, setRegion] = useState("Baghdad");

  // Iraqi regions list
  const iraqiRegions = [
    "Baghdad",
    "Basra",
    "Al-Anbar",
    "Babil (Babylon)",
    "Dhi Qar",
    "Al-Qadisiyyah (Diwaniyah)",
    "Diyala",
    "Dohuk",
    "Erbil",
    "Sulaymaniyah",
    "Halabja",
    "Kirkuk",
    "Karbala",
    "Najaf",
    "Nineveh (Mosul)",
    "Salah Al-Din",
    "Wasit",
    "Maysan",
    "Al-Muthanna"
  ];

  // Auto-detect location on component mount
  useEffect(() => {
    const reverseGeocodeAddress = async (lat, lng) => {
      try {
        // Wait for Google Maps to be fully loaded
        let retries = 0;
        while (!window.google?.maps?.Geocoder && retries < 20) {
          await new Promise(resolve => setTimeout(resolve, 500));
          retries++;
        }

        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat, lng } },
            (results, status) => {
              console.log("Geocoding status:", status);
              if (status === "OK" && results[0]) {
                setLocation(results[0].formatted_address);
              } else {
                console.warn("Geocoding failed, coordinates stored:", lat, lng);
              }
              setIsDetectingLocation(false);
            }
          );
        } else {
          console.error("Google Maps Geocoder not available after waiting");
          setIsDetectingLocation(false);
        }
      } catch (error) {
        console.error("Error reverse geocoding:", error);
        setIsDetectingLocation(false);
      }
    };

    const detectUserLocation = async () => {
      if (!navigator.geolocation) {
        console.log("Geolocation is not supported by this browser.");
        return;
      }

      setIsDetectingLocation(true);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng, accuracy } = position.coords;
          console.log("Detected coordinates:", lat, lng);
          console.log("Accuracy:", accuracy, "meters");
          console.log("Accuracy quality:", accuracy < 100 ? "Good (GPS)" : accuracy < 1000 ? "Fair (WiFi)" : "Poor (IP-based)");
          
          // Store coordinates
          setLatitude(lat);
          setLongitude(lng);
          
          // Reverse geocode to get address and display it
          await reverseGeocodeAddress(lat, lng);
        },
        (error) => {
          console.error("Error detecting location:", error);
          console.error("Error code:", error.code);
          console.error("Error message:", error.message);
          
          // Provide user-friendly error messages
          if (error.code === 1) {
            console.error("User denied location access. Please enable location permissions.");
          } else if (error.code === 2) {
            console.error("Location unavailable. Please check your device's location settings.");
          } else if (error.code === 3) {
            console.error("Location request timed out. Please try again.");
          }
          
          setIsDetectingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,  // Increased timeout to 15 seconds
          maximumAge: 0    // Don't use cached location
        }
      );
    };

    detectUserLocation();
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setLocationSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch coordinates from place ID
  const fetchCoordinatesFromPlaceId = async (placeId) => {
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const place = new Place({ id: placeId });
      await place.fetchFields({ fields: ["location"] });
      
      if (place.location) {
        return {
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  // Fetch location suggestions
  const fetchLocationSuggestions = async (input) => {
    if (!input || input.trim().length < 3) {
      setLocationSuggestions([]);
      return;
    }

    try {
      if (!window.google?.maps) {
        console.error("Google Maps not loaded");
        return;
      }

      const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }

      const request = {
        input: input,
        sessionToken: sessionTokenRef.current,
      };

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const formattedSuggestions = suggestions.map((suggestion) => ({
        description: suggestion.placePrediction.text.toString(),
        placeId: suggestion.placePrediction.placeId,
      }));

      setLocationSuggestions(formattedSuggestions);
    } catch (error) {
      console.error("Autocomplete suggestions error", error);
      setLocationSuggestions([]);
    }
  };

  // Handle location input change
  const handleLocationChange = (value, skipFetch = false) => {
    console.log("Location input changed to:", value);
    setLocation(value);
    if (!skipFetch) {
      fetchLocationSuggestions(value);
    }
  };


  // const handleOpenCamera = async () => {
  //   setShowCamera(true);
  //   try {
  //     const stream = await navigator.mediaDevices.getUserMedia({ video: true });
  //     if (videoRef.current) {
  //       videoRef.current.srcObject = stream;
  //       videoRef.current.play();
  //     }
  //   } catch (err) {
  //     console.error("Error accessing camera:", err);
  //     alert("Cannot access camera");
  //   }
  // };

  const handleOpenCamera = async () => {
    try {
      // Check if the device has any video input devices (camera)
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(
        (device) => device.kind === "videoinput"
      );

      if (!hasVideoInput) {
        setHasCamera(false);
        toast.error("No camera detected on this device.");
        return;
      }

      setHasCamera(true);
      setShowCamera(true);

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setHasCamera(false);
      toast.error(
        "Unable to access camera. Please check permissions or device settings."
      );
    }
  };

  const validateProfilePic = (file) => {
    if (!file) {
      setProfilePicError("Profile picture is required.");
      return false;
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      setProfilePicError("Invalid file type. Only JPG, JPEG, PNG allowed.");
      return false;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setProfilePicError("File is too large. Max size is 10MB.");
      return false;
    }

    setProfilePicError("");
    return true;
  };

  // Capture photo from camera
  const handleCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const file = new File([blob], "camera-photo.png", { type: "image/png" });

      if (validateProfilePic(file)) {
        setProfilePic(file);
        setProfilePicUrl(URL.createObjectURL(file));
      } else {
        setProfilePic(null);
        setProfilePicUrl("");
      }
    });

    // Stop camera
    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
  };
  const countryCodes = [
    { code: "+964", name: "Iraq" }
  ];
  // Handle file upload
  const handleProfilePicChange = (e) => {
    // const file = e.target.files[0];
    // setProfilePic(file);
    // if (file) {
    //   setProfilePicUrl(URL.createObjectURL(file));
    // }
    const file = e.target.files[0];
    if (!file) return;

    if (validateProfilePic(file)) {
      setProfilePic(file);
      setProfilePicUrl(URL.createObjectURL(file));
    } else {
      setProfilePic(null);
      setProfilePicUrl("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Form validation
    // if (form.checkValidity() === false || !profilePic) {
    //   e.stopPropagation();
    //   setValidated(true);
    //   return;
    // }
    if (form.checkValidity() === false || !profilePic) {
      e.stopPropagation();
      setValidated(true);
      if (!profilePic) setProfilePicError("Profile picture is required.");
      return;
    }

    if (profilePicError) {
      toast.error(profilePicError);
      return;
    }
    if (!location || location.trim() === "") {
      setLocationError("Location is required.");
      toast.error("Please select your location.");
      return;
    } else {
      setLocationError("");
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", profilePic);

      // Upload photo
      const uploadResponse = await axios.post(
        apiUrl('/professionals/photo'),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const profilePictureUrl = uploadResponse.data.data.key;

      // Prepare pharmacy data
      const pharmacyData = {
        role_id: 5,
        full_name: fullName,
        pharmacy_name: hospital, // use hospital field as Pharmacy/Medical Name
        location,
        latitude,
        longitude,
        online_consultation: onlineConsult === "Yes",
        contact_phone: `${countryCode}${phone}`,
        profile_picture_url: profilePictureUrl,
        short_bio: bio, // optional
        region: region,
      };

      await axios.post(apiUrl('/professionals'), pharmacyData, {
        headers: { "Content-Type": "application/json" },
      });

      console.log('Form submitted successfully!');
      console.log('REACT_APP_FRONTEND_URL:', process.env.REACT_APP_FRONTEND_URL);
      setShowModal(true);

      setFullName("");
      setProfilePic(null);
      setProfilePicUrl("");
      setHospital("");
      setLocation("");
      setLocationSuggestions([]);
      setOnlineConsult("No");
      setLatitude(null);
      setLongitude(null);
      setPhone("");
      setBio("");
      setRegion("Baghdad");
      setValidated(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success("Your details have been saved successfully");
    } catch (error) {
      console.error("Error submitting pharmacy data:", error);
      toast.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="main-container">
        <main className="main-content">
          <div className="content-wrapper">
            <div className="login-form-container">
              <h2 className="text-center">Pharmacy Details</h2>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                {/* Pharmacy Full Name */}
                <Form.Group className="form-group" controlId="pharmacyFullName">
                  <Form.Label>Pharmacist Full Name</Form.Label>
                  <Form.Control
                    className="form-input"
                    type="text"
                    placeholder="Enter Pharmacist full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Pharmacist full name is required
                  </Form.Control.Feedback>
                </Form.Group>

          

                <Form.Group className="form-group" controlId="profilePic">
                  <Form.Label>Pharmacy Picture</Form.Label>
                  <Row className="align-items-center">
                    <Col xs={3}>
                      <Image
                        src={profilePicUrl || Avatar}
                        roundedCircle
                        width={80}
                        height={80}
                        alt="Profile Preview"
                        style={{
                          objectFit: "cover",
                          border: "2px solid #00bcd4",
                        }}
                      />
                    </Col>
                    <Col>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Upload from Device
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          // onChange={(e) => {
                          //   const file = e.target.files[0];
                          //   setProfilePic(file);
                          //   if (file)
                          //     setProfilePicUrl(URL.createObjectURL(file));
                          // }}
                          onChange={handleProfilePicChange}
                        />
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleOpenCamera}
                        >
                          Open Camera
                        </Button>
                      </div>
                      {profilePicError && (
                        <div className="invalid-feedback d-block">
                          {profilePicError}
                        </div>
                      )}

                      {/* Camera Video Overlay */}
                      {/* {showCamera && (
                        <div style={{ marginTop: "1rem" }}>
                          <video
                            ref={videoRef}
                            style={{ width: "100%", maxHeight: 300 }}
                          />
                          <div className="d-flex justify-content-between mt-2">
                            <Button
                              variant="success"
                              onClick={handleCapturePhoto}
                            >
                              Capture
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => {
                                // Stop camera
                                const stream = videoRef.current?.srcObject;
                                if (stream)
                                  stream
                                    .getTracks()
                                    .forEach((track) => track.stop());
                                setShowCamera(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                          <canvas ref={canvasRef} style={{ display: "none" }} />
                        </div>
                      )} */}
                      {showCamera && (
                        <div style={{ marginTop: "1rem" }}>
                          {hasCamera ? (
                            <>
                              <video
                                ref={videoRef}
                                style={{ width: "100%", maxHeight: 300 }}
                              />
                              <div className="d-flex justify-content-between mt-2">
                                <Button
                                  variant="success"
                                  onClick={handleCapturePhoto}
                                >
                                  Capture
                                </Button>
                                <Button
                                  variant="danger"
                                  onClick={() => {
                                    const stream = videoRef.current?.srcObject;
                                    if (stream)
                                      stream
                                        .getTracks()
                                        .forEach((track) => track.stop());
                                    setShowCamera(false);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                              <canvas
                                ref={canvasRef}
                                style={{ display: "none" }}
                              />
                            </>
                          ) : (
                            <div className="text-danger mt-2">
                              Camera not available on this device.
                            </div>
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Form.Group>

                {/* Pharmacy / Medical Name */}
                <Form.Group className="form-group" controlId="hospitalName">
                  <Form.Label>Pharmacy Name</Form.Label>
                  <Form.Control
                    className="form-input"
                    type="text"
                    placeholder="Enter Pharmacy"
                    value={hospital}
                    onChange={(e) => setHospital(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Pharmacy name is required
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Location */}
                {/* <Form.Group className="form-group" controlId="location">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    className="form-input"
                    type="text"
                    placeholder="Enter pharmacy location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Location is required
                  </Form.Control.Feedback>
                </Form.Group> */}

                {/* <Form.Group className="form-group" controlId="location">
                  <Form.Label>Location</Form.Label>
                  <LocationPicker
                    onLocationSelect={(coords, address) => {
                      setLocation(address || `${coords.lat}, ${coords.lng}`);
                    }}
                  />
                  {locationError && (
                    <div className="invalid-feedback d-block">
                      {locationError}
                    </div>
                  )}
                </Form.Group> */}

                <Form.Group className="form-group" controlId="location">
                  <Form.Label>Location</Form.Label>
                  <div style={{ position: "relative" }} ref={dropdownRef}>
                    <div style={{ position: "relative" }}>
                      <MapPin
                        size={18}
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#6c757d",
                          zIndex: 1,
                          pointerEvents: "none"
                        }}
                      />
                      <Form.Control
                        className="form-input"
                        type="text"
                        placeholder={isDetectingLocation ? "Detecting your location..." : "Enter pharmacy location"}
                        value={location}
                        onChange={(e) => handleLocationChange(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        required
                        disabled={isDetectingLocation}
                        autoComplete="off"
                        style={{
                          paddingLeft: "40px",
                          borderRadius: locationSuggestions.length > 0 ? "8px 8px 0 0" : "8px",
                          border: locationSuggestions.length > 0 ? "1px solid #0d6efd" : "1px solid #ced4da",
                          transition: "all 0.2s ease"
                        }}
                      />
                    </div>
                    {locationSuggestions.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          zIndex: 1000,
                          width: "100%",
                          backgroundColor: "#fff",
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          borderRadius: "0 0 8px 8px",
                          maxHeight: 280,
                          overflowY: "auto",
                          boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                        }}
                      >
                        {locationSuggestions.map((suggestion, idx) => (
                          <div
                            key={suggestion.placeId}
                            style={{
                              padding: "12px 16px",
                              cursor: "pointer",
                              borderBottom: idx === locationSuggestions.length - 1 ? "none" : "1px solid #f0f0f0",
                              background: "#fff",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                              transition: "all 0.2s ease",
                            }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              handleLocationChange(suggestion.description, true);
                              const coords = await fetchCoordinatesFromPlaceId(suggestion.placeId);
                              if (coords) {
                                setLatitude(coords.latitude);
                                setLongitude(coords.longitude);
                              }
                              setLocationSuggestions([]);
                              sessionTokenRef.current = null;
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#f8f9fa";
                              e.currentTarget.style.paddingLeft = "20px";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#fff";
                              e.currentTarget.style.paddingLeft = "16px";
                            }}
                          >
                            <MapPin
                              size={18}
                              style={{
                                color: "#0d6efd",
                                marginTop: "2px",
                                flexShrink: 0
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: "14px",
                                color: "#212529",
                                lineHeight: "1.5",
                                fontWeight: "400"
                              }}>
                                {suggestion.description}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Form.Control.Feedback type="invalid">
                    Location is required
                  </Form.Control.Feedback>
                  {locationError && (
                    <div className="invalid-feedback d-block">
                      {locationError}
                    </div>
                  )}
                  
                  {/* Map with styled container */}
                  <div 
                    style={{
                      marginTop: "20px",
                      border: "4px solid #0dd0d0",
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(13,208,208,0.2)"
                    }}
                  >
                    <LocationPicker
                      onLocationSelect={(coords, address) => {
                        setLocation(address || `${coords.lat}, ${coords.lng}`);
                        setLatitude(coords.lat);
                        setLongitude(coords.lng);
                      }}
                      hideButtons={true}
                      externalPosition={latitude && longitude ? { lat: latitude, lng: longitude } : null}
                    />
                  </div>
                </Form.Group>
                {/* <LocationMap/> */}

                {/* <Form.Group className="form-group" controlId="location">
  <Form.Label>Location</Form.Label>
  <LocationPicker
    onLocationSelect={(coords, address) => {
      console.log('address:', address);
      if (address) {
        setLocation(address); // save human-readable address
      }
    }}
  />
  {locationError && (
    <div className="invalid-feedback d-block">{locationError}</div>
  )}
</Form.Group> */}
                {/* Online Consultation */}
                <Form.Group className="form-group" controlId="onlineConsult">
                  <Form.Label>
                    Are you interested in online Pharmacy?
                  </Form.Label>
                  <div style={{ marginTop: "0.5rem" }}>
                    <ToggleButtonGroup
                      type="radio"
                      name="onlineConsult"
                      value={onlineConsult}
                      onChange={(val) => setOnlineConsult(val)}
                    >
                      <ToggleButton
                        id="online-yes"
                        value="Yes"
                        variant={
                          onlineConsult === "Yes"
                            ? "primary"
                            : "outline-primary"
                        }
                        style={{ minWidth: 80 }}
                      >
                        Yes
                      </ToggleButton>
                      <ToggleButton
                        id="online-no"
                        value="No"
                        variant={
                          onlineConsult === "No" ? "primary" : "outline-primary"
                        }
                        style={{ minWidth: 80 }}
                      >
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                </Form.Group>


                <Form.Group
                  className="form-group form-group-check"
                  controlId="contactPhone"
                >
                  <Form.Label>Contact Phone</Form.Label>
                  <Row>
                    <Col xs={5}>
                      <Form.Select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        required
                        style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {countryCodes.map((c, index) => (
                          <option key={`${c.code}-${c.name}-${index}`} value={c.code}>
                            {c.code} - {c.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={7}>
                      <Form.Control
                        className="form-input"
                        type="tel"
                        placeholder="Enter phone"
                        value={phone}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (!/^[0-9]*$/.test(val)) return; // allow only numbers
                          setPhone(val);
                        }}
                        required
                        pattern="^[0-9]{8,15}$" // only digits allowed
                      />
                    </Col>
                  </Row>
                  <Form.Control.Feedback type="invalid">
                    Please enter a valid phone number (8–15 digits)
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Region Dropdown */}
                <Form.Group className="form-group" controlId="region">
                  <Form.Label>Region</Form.Label>
                  <div className="select-wrapper">
                    <Form.Select
                      className="form-select"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      required
                    >
                      {iraqiRegions.map((regionName) => (
                        <option key={regionName} value={regionName}>
                          {regionName}
                        </option>
                      ))}
                    </Form.Select>
                    <span className="select-icon">&#9662;</span>
                  </div>
                </Form.Group>

                {/* Short Bio (optional) */}
                <Form.Group className="form-group" controlId="bio">
                  <Form.Label>
                    Short Bio / Years of Experience in Pharmacy (optional)
                  </Form.Label>
                  <Form.Control
                    className="form-input"
                    as="textarea"
                    rows={3}
                    placeholder="Tell us about your experience"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                  />
                </Form.Group>

                <Button variant="primary" type="submit" className="login-btn" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Form>
            </div>
          </div>
        </main>
      </div>
      <CustomModal 
        showModal={showModal} 
        onClose={() => setShowModal(false)} 
        autoRedirect={true}
        redirectUrl={process.env.REACT_APP_FRONTEND_URL}
      />
    </>
  );
};

export default PharmacyForm;
